import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Enquiry", "Confirmed", "Completed", "Cancelled"];
const STATUS_STYLES = {
  Enquiry: "bg-amber-50 text-amber-800",
  Confirmed: "bg-emerald-50 text-emerald-800",
  Completed: "bg-[#C5A059]/15 text-[#9E7B3A]",
  Cancelled: "bg-red-50 text-red-700",
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export default function Bookings() {
  const [bookings, setBookings] = useState([]);

  const load = () => api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (id, updates) => {
    try {
      await api.put(`/admin/bookings/${id}`, updates);
      toast.success("Booking updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-bookings-page">
      <h1 className="font-serif text-3xl md:text-4xl font-light mb-2">Bookings</h1>
      <p className="text-sm text-[#4A4A4A] mb-10">Beauty service enquiries — confirm deposits and appointments here.</p>

      <div className="bg-white border border-[#C5A059]/25 overflow-x-auto">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead>
            <tr className="border-b border-[#C5A059]/20 text-left">
              {["Received", "Client", "Service", "Preferred", "Price", "Deposit", "Status"].map((h) => (
                <th key={h} className="px-5 py-4 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#C5A059]/10">
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`}>
                <td className="px-5 py-4 text-[#4A4A4A] whitespace-nowrap">{fmtDate(b.created_at)}</td>
                <td className="px-5 py-4">
                  <p className="font-medium">{b.name}</p>
                  <p className="text-xs text-[#4A4A4A]">{b.email}</p>
                  {b.phone && <p className="text-xs text-[#4A4A4A]">{b.phone}</p>}
                  {b.message && <p className="text-xs text-[#4A4A4A] italic mt-1">"{b.message}"</p>}
                </td>
                <td className="px-5 py-4">{b.service}</td>
                <td className="px-5 py-4 text-[#4A4A4A]">{b.preferred || "—"}</td>
                <td className="px-5 py-4">${b.price?.toFixed(0)}</td>
                <td className="px-5 py-4">
                  <button onClick={() => update(b.id, { deposit_paid: !b.deposit_paid })} data-testid={`deposit-toggle-${b.id}`}
                    className={`text-[10px] tracking-widest uppercase px-2.5 py-1 border transition-colors duration-300 ${
                      b.deposit_paid ? "border-emerald-700 text-emerald-800 bg-emerald-50" : "border-[#C5A059]/40 text-[#4A4A4A] hover:border-[#9E7B3A]"
                    }`}>
                    {b.deposit_paid ? `$${b.deposit?.toFixed(0)} Paid` : `$${b.deposit?.toFixed(0)} Pending`}
                  </button>
                </td>
                <td className="px-5 py-4">
                  <Select value={b.status} onValueChange={(v) => update(b.id, { status: v })}>
                    <SelectTrigger data-testid={`booking-status-${b.id}`} className={`rounded-none border-0 text-[10px] tracking-widest uppercase w-32 ${STATUS_STYLES[b.status] || ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {bookings.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-[#4A4A4A]" data-testid="no-bookings-row">No bookings yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
